export const FACILITY_QR_FIELDS = ["facility_name", "location", "facility_type", "building_name", "floor", "part_name", "upload_id"];
export const FACILITY_QR_DISPLAY_FIELDS = ["facility_name", "building_name", "floor", "location", "part_name", "facility_type"];

export function getFacilityQrMetadata(data = {}) {
  return Object.fromEntries(FACILITY_QR_FIELDS.map(key => [key, String(data?.[key] ?? "")]));
}
export function isValidFacilityUploadId(value) {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value ?? "");
}
export function decodeNodeQrId(value) {
  if (typeof value !== "string") return null;
  const text = value.trim();
  if (isValidFacilityUploadId(text)) return text.toLowerCase();
  if (!/^[A-Za-z0-9_-]{22}$/.test(text)) return null;
  try {
    const bytes = atob(text.replace(/-/g, "+").replace(/_/g, "/") + "==");
    if (bytes.length !== 16 || btoa(bytes).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "") !== text) return null;
    const hex = [...bytes].map(byte => byte.charCodeAt(0).toString(16).padStart(2, "0")).join("");
    const id = `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
    return isValidFacilityUploadId(id) ? id : null;
  } catch { return null; }
}
export function encodeNodeQrId(value) {
  if (isShortNodeQrId(value)) return value.trim();
  const id = decodeNodeQrId(value);
  if (!id) return "";
  const bytes = id.replace(/-/g, "").match(/../g).map(byte => String.fromCharCode(parseInt(byte, 16))).join("");
  return btoa(bytes).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}
export const isShortNodeQrId = value => typeof value === "string" && /^[A-Za-z0-9]{11}$/.test(value.trim());
export const readNodeQrReference = value => isShortNodeQrId(value) ? value.trim() : decodeNodeQrId(value);

function parseQrUrl(value, origin) {
  const text = value.trim();
  return new URL(/^(?:localhost(?::\d+)?|[a-z0-9.-]+\.[a-z]{2,})(?::\d+)?\//i.test(text) ? `https://${text}` : text, origin);
}
export function readNodeQrInput(value) {
  if (typeof value !== "string") return null;
  const direct = readNodeQrReference(value);
  if (direct) return direct;
  try {
    if (value.trim().startsWith("{")) return readNodeQrReference(JSON.parse(value).qr_id);
    const url = parseQrUrl(value, window.location.origin);
    return parseFacilityQrMetadata(url.search, url.pathname).node_id;
  } catch { return null; }
}
export function parseFacilityQrMetadata(search, pathname = "") {
  const params = new URLSearchParams(search);
  const short = /^\/q\/([A-Za-z0-9]{11})\/?$/.exec(pathname);
  return { node_id: short?.[1] || readNodeQrReference(params.get("qr_id") || params.get("node_id")) };
}
export function buildFacilityQrUrl(data, origin) {
  if (isShortNodeQrId(data?.qr_id)) return new URL(`/q/${data.qr_id}`, origin).toString();
  const id = encodeNodeQrId(data?.node_id);
  if (!id) return "";
  const url = new URL("/qr/facility", origin);
  url.searchParams.set("qr_id", id);
  return url.toString();
}
export function buildFacilityQrPayload(data, origin = window.location.origin) {
  return buildFacilityQrUrl(data, origin).replace(/^https?:\/\//, "");
}
export function getFacilityQrScanPath(value, origin) {
  if (typeof value !== "string" || value.length > 20000) return null;
  try {
    const fields = value.trim().startsWith("{") ? JSON.parse(value) : null;
    const url = parseQrUrl(fields ? fields.url : value, origin);
    const allowedHost = url.host === new URL(origin).host || url.host === "checklab.co.kr";
    if (!allowedHost || !/^https?:$/.test(url.protocol) ||
      (url.pathname !== "/qr/facility" && !/^\/q\/[A-Za-z0-9]{11}\/?$/.test(url.pathname)) || url.username || url.password) return null;
    const metadata = parseFacilityQrMetadata(url.search, url.pathname);
    if (!metadata.node_id || (fields && readNodeQrReference(fields.qr_id) !== metadata.node_id)) return null;
    return `${url.pathname}${url.search}`;
  } catch { return null; }
}
export function getFacilityQrReturnPath(value) {
  if (typeof value !== "string" || !value.startsWith("/") || value.startsWith("//")) return null;
  try {
    const url = new URL(value, "https://qr.invalid");
    return url.origin === "https://qr.invalid" && (url.pathname === "/qr/facility" || /^\/q\/[A-Za-z0-9]{11}\/?$/.test(url.pathname)) ? `${url.pathname}${url.search}` : null;
  } catch { return null; }
}
